import clsx from 'clsx';

import Texture from '@commons/components/Texture';
import Flex from '@commons/containers/Flex';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import { breakpoints } from '@commons/contexts/responsive';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalImage, DrupalSection, DrupalVideo } from '@commons/types/drupal';
import type { Alignment, Breakpoint, Spacing, Theme, Width } from '@commons/types/styles';

import MediaBackground from '../Background/Media';
import { type SectionMinHeightType, getSectionMinHeight } from '../helpers';
import ContentRegion from './Content';
import styles from './index.module.scss';

// Type definition for the PartialFluidSection component properties.

type Props = DrupalSection<{
  appearance: {
    theme: Theme;
  };
  container: {
    contentWidth: Width;
    minHeightType: SectionMinHeightType;
    customMinHeight?: string;
  };
  layout: {
    columnToBeFluid: 1 | 2;
    mobileBreakpoint: Breakpoint;
    mobileReverse: boolean;
  };
  content: {
    padding: Spacing;
    horizontalAlignment: Alignment;
    verticalAlignment: Alignment;
    texture: boolean;
    animatedTexture: boolean;
  };
  fluid: {
    backgroundMedia: DrupalImage | DrupalVideo;
    mobileBackgroundMedia?: DrupalImage | DrupalVideo;
    overlayOpacity: number;
    hideFluidOnMobile: boolean;
  };
}>;

/**
 * PartialFluid section component.
 *
 * IMPORTANT:
 * - This component should be a server component, otherwise everything it renders and itself will be client components.
 *   Since it is used on many pages, this would bloat the JavaScript bundle significantly.
 */

const PartialFluidSection = ({ type, blocks, weight, settings }: Props) => {
  const { appearance, container, layout, content, fluid } = settings;

  // Extract the settings.

  const { contentWidth, minHeightType, customMinHeight } = container;

  const { columnToBeFluid, mobileBreakpoint, mobileReverse } = layout;

  const { padding, horizontalAlignment, verticalAlignment, texture, animatedTexture } = content;

  const { backgroundMedia, mobileBackgroundMedia, overlayOpacity, hideFluidOnMobile } = fluid;

  const isFluidFirst = columnToBeFluid == 1;

  const minHeight = getSectionMinHeight(minHeightType, customMinHeight);

  // Render the content column with its blocks and texture.

  const sortedBlocks = PageBuilder.sortBlocksByWeight(blocks);
  const renderedBlocks = sortedBlocks.map(PageBuilder.renderBlock);

  const renderedContentRegion = PageBuilder.renderRegion(
    'content',
    ContentRegion,
    {
      direction: 'column',
      width: { xs: 12, [mobileBreakpoint]: 6 },
      align: horizontalAlignment,
      justify: verticalAlignment,
      children: renderedBlocks,
      paddingTop: padding,
      paddingBottom: padding,
      style: {
        minHeight,
        paddingLeft: isFluidFirst ? `calc(var(--spacing-${padding}) / 2)` : 0,
        paddingRight: isFluidFirst ? 0 : `calc(var(--spacing-${padding}) / 2)`,
      },
      key: 'content-region',
    },
    { type, weight }
  );

  const renderedTexture = texture && (
    <Texture
      animated={animatedTexture}
      className={clsx(styles.texture, styles[isFluidFirst ? 'end' : 'start'])}
    />
  );

  // Render the fluid column with its background.

  const isFirstSection = weight === 0;
  const renderedFluidColumn = (
    <MediaBackground
      media={backgroundMedia}
      breakpoint={mobileBreakpoint}
      mobileMedia={mobileBackgroundMedia}
      overlayOpacity={overlayOpacity}
      sizes={`(max-width: ${breakpoints[mobileBreakpoint]}px) 100vw, 50vw`}
      priority={isFirstSection}
      className={clsx(styles.fluidColumn, hideFluidOnMobile && styles.hideOnMobile)}
      style={{ height: minHeight }}
      key="fluid-column"
    />
  );

  // Reorder the columns if needed.

  const renderedSections = [renderedContentRegion, renderedFluidColumn];
  if (isFluidFirst) renderedSections.reverse();

  // Preparing the containers properties.

  const regionsAlignment = isFluidFirst ? 'end' : 'start';

  const classes = clsx(styles.section, styles[`mobile-${mobileBreakpoint}`]);
  const regionsClasses = clsx(styles.regions, 'h-100');

  return (
    <ResponsiveContainer
      theme={appearance.theme}
      paddingTop="none"
      paddingBottom="none"
      horizontalAlignment="center"
      outsideChildren={renderedTexture}
      className={classes}
      contentClassName={styles.sectionContent}
    >
      <Flex
        width={{ xs: 12, lg: contentWidth }}
        justify={{
          xs: hideFluidOnMobile ? verticalAlignment : regionsAlignment,
          [mobileBreakpoint]: regionsAlignment,
        }}
        direction={{ xs: mobileReverse ? 'column-reverse' : 'column', [mobileBreakpoint]: 'row' }}
        className={regionsClasses}
      >
        {renderedSections}
      </Flex>
    </ResponsiveContainer>
  );
};

export default PartialFluidSection;
